import axios from 'axios';
import { notifyError } from 'config/setupBugsnag';
import getConfig from 'next/config';
import { marketingProjectService, productService } from 'services';
import {
  CreateExportProjectJobServerResponse,
  DHGetProjectServerResponse,
  DHListProjectsServerResponse,
  DHProjectClient,
  DHProjectServerResponse,
  GetExportProjectJobStatusServerResponse,
} from 'types';
import { poll } from 'utils';
import RestUtility from '../../restUtility';
import { designHuddleTokenEndPoint } from '../../restUtility/restEndpoints';

export type DesignHuddleTokenData = {
  access_token: string;
  token_type: string;
  expires_in: number;
  refresh_token: string;
};

export class DesignHuddleService {
  private restUtility: RestUtility;

  constructor() {
    this.restUtility = new RestUtility();
  }

  async getDHToken() {
    try {
      const dhAccessToken = sessionStorage.getItem('dhAccessToken');
      const dhAccessTokenExpiresIn = sessionStorage.getItem(
        'dhAccessTokenExpiresIn'
      );
      if (dhAccessToken !== null && dhAccessTokenExpiresIn !== null) {
        const currentTime = Math.floor(Date.now() / 1000);
        if (currentTime < parseInt(dhAccessTokenExpiresIn)) {
          return dhAccessToken;
        }
      }

      const tokenData = await this.restUtility.post<DesignHuddleTokenData>(
        designHuddleTokenEndPoint,
        {}
      );
      const tokenExpiresIn =
        Math.floor(Date.now() / 1000) + tokenData.expires_in;
      sessionStorage.setItem('dhAccessToken', tokenData.access_token);
      sessionStorage.setItem(
        'dhAccessTokenExpiresIn',
        tokenExpiresIn.toString()
      );
      return tokenData.access_token;
    } catch (error) {
      notifyError('ApiError:: getDHToken', error);
      throw error;
    }
  }

  async getRequestHeader() {
    const dhAccessToken = await this.getDHToken();
    return {
      headers: {
        Authorization: `Bearer ${dhAccessToken}`,
      },
    };
  }

  getDHDomain() {
    const { publicRuntimeConfig } = getConfig();
    return `https://${publicRuntimeConfig.DESIGN_HUDDLE_DOMAIN}`;
  }

  async createProject(clinid: string | undefined, sku: string) {
    try {
      const requestHeader = await this.getRequestHeader();

      const newProject = await axios.post(
        `${this.getDHDomain()}/api/projects?source_template_code=${sku}`,
        {
          template_code: sku,
        },
        requestHeader
      );
      const dhProjectId = newProject.data.data.project_id;
      return dhProjectId;
    } catch (error) {
      notifyError('ApiError:: createProject', error);
      throw error;
    }
  }

  async getProjects(clinid: string | undefined, sku?: string) {
    const requestHeader = await this.getRequestHeader();
    let skuFilter = '';

    if (sku) {
      skuFilter = `source_template_code=${sku}`;
    }

    const projects = await axios.get<DHListProjectsServerResponse>(
      `${this.getDHDomain()}/api/projects?${skuFilter}`,
      requestHeader
    );

    return this.buildProjects(projects.data.data.items);
  }

  async getProject(clinid: string | undefined, projectId: string) {
    try {
      const requestHeader = await this.getRequestHeader();

      const project = await axios.get<DHGetProjectServerResponse>(
        `${this.getDHDomain()}/api/projects/${projectId}`,
        requestHeader
      );

      return this.buildProject(project.data.data);
    } catch (error) {
      notifyError('ApiError:: getProject', error);
      throw error;
    }
  }

  async deleteProject(
    clinid: string | undefined,
    projectId: string,
    isDeleteReviewRequest: boolean
  ) {
    const requestHeader = await this.getRequestHeader();
    try {
      if (isDeleteReviewRequest) {
        await marketingProjectService.deleteProject(projectId);
      }

      await axios.delete(
        `${this.getDHDomain()}/api/projects/${projectId}`,
        requestHeader
      );
    } catch (error) {
      notifyError('ApiError:: deleteProject', error);
      throw error;
    }
  }

  async createExportProjectJob(
    clinid: string | undefined,
    projectId: string,
    format: string,
    sku?: string,
    shouldCreatDownloadHistory?: boolean
  ) {
    const requestHeader = await this.getRequestHeader();
    try {
      const response = await axios.post<CreateExportProjectJobServerResponse>(
        `${this.getDHDomain()}/api/projects/${projectId}/export`,
        {
          format: format,
        },
        requestHeader
      );
      const jobId = response.data.data.job_id;
      const { thumbnailUrl, downloadUrl } =
        await this.getExportProjectJobStatus(clinid, projectId, jobId);
      if (downloadUrl && shouldCreatDownloadHistory && sku) {
        await productService.downloadAsset(sku, projectId);
      }
      return { thumbnailUrl, downloadUrl };
    } catch (error) {}
  }

  async getExportProjectJobStatus(
    clinid: string | undefined,
    projectId: string,
    jobId: string
  ) {
    const requestHeader = await this.getRequestHeader();

    const getJobStatus = (requestHeader: any) => {
      return axios.get<GetExportProjectJobStatusServerResponse>(
        `${this.getDHDomain()}/api/projects/${projectId}/export/jobs/${jobId}`,
        requestHeader
      );
    };

    const shouldReRun = (result: any) => {
      return !result.data.data.completed;
    };

    const response = await poll(
      () => getJobStatus(requestHeader),
      shouldReRun,
      1000
    );

    return {
      thumbnailUrl: response.data.data.thumbnail_url,
      downloadUrl: response.data.data.download_url,
    };
  }

  buildProjects(projects: DHProjectServerResponse[]): DHProjectClient[] {
    return projects.map(item => ({
      thumbnailUrl: item.thumbnail_url,
      projectId: item.project_id,
      status: item?.project_approval_status?.project_approval_status_id,
      comment: item?.project_approval_status?.project_approval_log?.comment,
    }));
  }

  buildProject(response: DHProjectServerResponse) {
    const project = {
      projectTitle: '',
      pageCount: 0,
      dateCreated: '',
      dateUpdated: '',
      lastEdited: '',
      lastOpenedNull: null,
      user: {
        userId: 0,
        emailAddress: '',
        firstName: '',
        lastName: '',
        currentUser: false,
      },
      sourceTemplate: {
        sourceTemplateId: 0,
        sourceTemplateCode: '',
        sourceTemplateTitle: '',
        primaryTemplateCategoryId: 0,
        primaryTemplateCategoryItemId: 0,
        sourceTemplateVersionId: 0,
      },
      projectId: '',
      thumbnailUrl: '',
      tags: [],
      mediaType: {
        mediaTypeId: '',
        mediaTypeName: '',
      },
      projectStatus: {
        projectStatusId: '',
        projectStatusName: '',
      },
      projectApprovalStatus: {
        projectApprovalStatusId: '',
        projectApprovalStatusName: '',
        projectApprovalLog: {
          comment: '',
          dateCreated: '',
          projectApprovalSubmissionId: '',
        },
      },
      dimensions: {
        width: 0,
        height: 0,
        unitType: {
          unitTypeId: '',
          unitTypeName: '',
        },
      },
    };

    if (response) {
      project.projectTitle = response.project_title;
      project.pageCount = response.page_count;
      project.dateCreated = response.date_created;
      project.dateUpdated = response.date_updated;
      project.lastEdited = response.last_edited;
      project.lastOpenedNull = response.last_opened;
      project.user = {
        userId: response.user.user_id,
        emailAddress: response.user.email_address,
        firstName: response.user.first_name,
        lastName: response.user.last_name,
        currentUser: response.user.current_user,
      };
      project.sourceTemplate = {
        sourceTemplateId: response.source_template.source_template_id,
        sourceTemplateCode: response.source_template.source_template_code,
        sourceTemplateTitle: response.source_template.source_template_title,
        primaryTemplateCategoryId:
          response.source_template.primary_template_category_id,
        primaryTemplateCategoryItemId:
          response.source_template.primary_template_category_item_id,
        sourceTemplateVersionId:
          response.source_template.source_template_version_id,
      };
      project.projectId = response.project_id;
      project.thumbnailUrl = response.thumbnail_url;
      project.tags = response.tags;
      project.mediaType = {
        mediaTypeId: response.media_type.media_type_id,
        mediaTypeName: response.media_type.media_type_name,
      };
      project.projectStatus = {
        projectStatusId: response.project_status.project_status_id,
        projectStatusName: response.project_status.project_status_name,
      };
      project.dimensions = {
        width: response.dimensions.width,
        height: response.dimensions.height,
        unitType: {
          unitTypeId: response.dimensions.unit_type.unit_type_id,
          unitTypeName: response.dimensions.unit_type.unit_type_name,
        },
      };
    }

    return project;
  }
}
